/**
 * The daily "Use it or toss it" card (core foodroundup.ts; owner, 2026-10-08): "3 things to use or toss today", the foods with their
 * pictures, grouped past its date · today · tomorrow · this week. Shown on Today and on Food to the people who chose the in-app
 * roundup (managers and homes by default). Nothing due: no card. Older servers or any failure: no card, never an error.
 */
import { useState } from "react";
import type { FoodScope } from "../api.ts";
import { useLoad } from "../lib/useLoad.ts";
import { useApi } from "../session.tsx";
import { FoodThumb, PhotoLarge } from "./FoodPhoto.tsx";
import { Card, LinkButton } from "./ui.tsx";

export function FoodRoundupCard({ scope, onFood }: { scope: FoodScope; onFood?: boolean }) {
  const api = useApi();
  const [large, setLarge] = useState<{ url: string; name: string }>();
  const d = useLoad(async () => {
    if (typeof api.foodRoundup !== "function") return null;
    const [r, p] = await Promise.all([api.foodRoundup(scope), api.foodRoundupPrefs(scope)]);
    return p.prefs.enabled && p.prefs.inApp && r.due > 0 ? r : null;
  }, [api, scope.orgId, scope.locationId, scope.home]);
  const r = d.data;
  if (!r) return null;
  return (
    <Card className="stack-sm food-roundup" edge={r.groups[0]!.items.length ? "stop" : "warn"}>
      {large && <PhotoLarge url={large.url} name={large.name} onClose={() => setLarge(undefined)} />}
      <p className="kicker">Use it or toss it</p>
      <h2 data-testid="food-roundup-line">{r.line}</h2>
      {r.groups.filter((g) => g.items.length).map((g) => (
        <section key={g.key} aria-label={g.title} data-group={g.key} className="stack-sm">
          <h3 className="small"><strong>{g.title}</strong></h3>
          <ul className="plain food-roundup-items">
            {g.items.map((it) => (
              <li key={it.recordId} className="food-history-row">
                <FoodThumb r={{ id: it.recordId, name: it.name, photoId: it.photoId }} scope={scope} onOpen={(url) => setLarge({ url, name: it.name })} />
                <span><strong>{it.name}</strong><br /><span className="small">{it.where} · {it.line}</span></span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {!onFood && <div><LinkButton to="/food" icon="chevron" iconEnd>Open the food list</LinkButton></div>}
    </Card>
  );
}
